import { APPOINTMENT_STATUS_EDGES } from "@dentacore/shared-types";
import type { AppointmentStatusName } from "@dentacore/shared-types";

/**
 * The status graph, in the browser — **a mirror, never the decision.**
 *
 * The server owns this rule (`apps/api/src/appointments/status-graph.ts`) and
 * refuses anything illegal whatever the screen does. This copy exists only so a
 * panel can offer the three or four moves that are actually available instead
 * of all seven and letting the user find out by being refused.
 *
 * **It no longer has to keep mirroring by hand.** The table moved to
 * `@dentacore/shared-types` on 2026-08-31 and both sides import it, so an edge
 * cannot exist on one side and not the other. Until then this file carried its
 * own copy with a comment asking whoever edited one to remember the other —
 * which is a promise rather than a mechanism, and the same shape that let the
 * CSV price importer disagree with the browser by a factor of a hundred.
 *
 * P3 Q3 for the shape, P3 Q14 for what leaves `NO_SHOW`, T3 for the step back.
 */

export function nextStatuses(from: string): readonly string[] {
  return APPOINTMENT_STATUS_EDGES[from as AppointmentStatusName] ?? [];
}

/**
 * `NOT_CHECKED_IN` → `Not checked in`. Sentence case, never uppercase.
 *
 * **`NO_SHOW` is hyphenated**, because working plan §6.2 writes it that way —
 * the same special case the server makes, for the same reason.
 */
export function statusLabel(status: string): string {
  if (status === "NO_SHOW") return "No-show";

  const words = status.toLowerCase().replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * The verb for moving *to* a status, for a button.
 *
 * "Cancel" reads better than "Cancelled" on something you press, and "Check in"
 * better than "Checked in" — a button says what it will do, not what the thing
 * will become.
 */
/**
 * **A move's verb depends on the direction, not only the destination** (T3).
 *
 * Before the graph could go backwards, the target alone was enough: there was
 * one way to reach `CHECKED_IN` and it was "Check in". T3 added a second, from
 * `IN_CHAIR` — and labelling *that* "Check in" would be actively wrong, since
 * the patient has been checked in for twenty minutes and what you are doing is
 * taking them out of the chair. `CONFIRMED` has the same problem: reached from
 * `CHECKED_IN` it is an undo, not a confirmation.
 *
 * So the three reversals get undo wording and everything else is unchanged.
 * "Un-confirm" is the client's own phrase for the case they reported.
 */
const UNDOING: Record<string, string> = {
  "CONFIRMED>NOT_CHECKED_IN": "Un-confirm",
  "CHECKED_IN>CONFIRMED": "Undo check-in",
};

export function statusAction(status: string, from?: string): string {
  const undo = from === undefined ? undefined : UNDOING[`${from}>${status}`];
  if (undo !== undefined) return undo;

  switch (status) {
    case "CONFIRMED":
      return "Confirm";
    case "CHECKED_IN":
      // Reached forward, or from NO_SHOW when they turned up after all — both
      // are genuinely "check this person in", so no special case is needed.
      return "Check in";
    case "IN_CHAIR":
      return "Start treatment";
    case "COMPLETED":
      return "Complete visit";
    case "NO_SHOW":
      return "Mark no-show";
    case "CANCELLED":
      return "Cancel appointment";
    default:
      return statusLabel(status);
  }
}
